/**
 * Aureliseé, koopknoppen op de productpagina (blocks/au-ritual-buy.liquid).
 *
 * - Twee knoppen (sinds 11 oktober 2026): links Add to cart voegt dit product meteen
 *   toe; rechts Get your ritual klapt het paneel met de stappen open en dicht
 *   (data-au-mode op het blok: single of ritual).
 * - Change klapt de keuzelijst van een stap open, Select kiest een product: het
 *   kaartje van die stap wisselt, de som rechts in keuze 2 loopt mee.
 * - De grote knop Add to cart onder de stappen zet alle drie de producten als
 *   losse regels in het mandje. Het mandje werkt Vessel zelf bij via de eigen
 *   gebeurtenis van het thema (zelfde weg als in au-cart.js).
 * - Vast balkje onderin ([data-au-bar]): wordt naar de body verplaatst en gaat
 *   aan zodra de echte knop Add to cart boven uit beeld is gescrold. Prijs en
 *   knoptekst lopen mee met de keuze; de knop erin gebruikt dezelfde add().
 *
 * Zie snippets/au-bouwlog.liquid.
 */

import { CartAddEvent } from '@theme/events';

/** @returns {string} De secties die het mandje tonen, komma-gescheiden. */
function cartSections() {
  const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
  return [...new Set(ids)].join(',');
}

/**
 * Bedrag in centen naar de geldopmaak van de winkel (shop.money_format).
 * @param {number} cents
 * @param {string} format
 */
function money(cents, format) {
  const amount = cents / 100;
  const parts = (value, decimals, thousands, decimal) => {
    const fixed = value.toFixed(decimals);
    const [whole, frac] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac !== undefined ? grouped + decimal + frac : grouped;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
    switch (key) {
      case 'amount':
        return parts(amount, 2, ',', '.');
      case 'amount_no_decimals':
        return parts(amount, 0, ',', '.');
      case 'amount_with_comma_separator':
        return parts(amount, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return parts(amount, 0, '.', ',');
      case 'amount_with_space_separator':
        return parts(amount, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return parts(amount, 0, ' ', ',');
      case 'amount_with_apostrophe_separator':
        return parts(amount, 2, "'", '.');
      default:
        return parts(amount, 2, ',', '.');
    }
  });
}

/** @param {HTMLElement} root */
function mode(root) {
  return root.dataset.auMode === 'ritual' ? 'ritual' : 'single';
}

/** @param {HTMLElement} root */
function currentVariant(root) {
  /* de variantkiezer van Vessel schrijft de gekozen variant in dit veld */
  const input = document.querySelector('product-form-component input[ref="variantId"]');
  if (input instanceof HTMLInputElement && input.value) return input.value;
  return root.dataset.auVariant || '';
}

/** @param {HTMLElement} root */
function syncTotal(root) {
  const total = root.querySelector('[data-au-total]');
  const was = root.querySelector('[data-au-was]');
  if (!total) return;
  let sum = 0;
  for (const box of root.querySelectorAll('[data-au-stepbox]')) {
    sum += Number(box.getAttribute('data-au-price') || 0);
  }
  /* zelfde rekensom als in Liquid: som maal (100 - percentage), gedeeld door 100, afgerond naar beneden */
  const percent = Number(root.dataset.auPercent || 0);
  const discounted = Math.floor((sum * (100 - percent)) / 100);
  const format = root.dataset.auMoney || '€{{amount_with_comma_separator}}';
  total.textContent = money(discounted, format);
  if (was) was.textContent = money(sum, format);
  syncBar(root);
}

/** @param {HTMLElement} root */
function syncMode(root) {
  const ritual = mode(root) === 'ritual';
  const panel = root.querySelector('[data-au-panel]');
  if (panel) panel.hidden = !ritual;
  for (const pick of root.querySelectorAll('[data-au-pick]')) {
    const on = (pick.getAttribute('data-au-pick') === 'ritual') === ritual;
    pick.classList.toggle('au-buy__btn--on', on);
    pick.setAttribute('aria-pressed', String(on));
    if (pick.getAttribute('data-au-pick') === 'ritual') pick.setAttribute('aria-expanded', String(ritual));
  }
  const label = ritual ? root.dataset.auAddRitual || '' : root.dataset.auAddSingle || '';
  for (const add of addButtons(root)) add.textContent = label;
  syncBar(root);
}

/** Alle knoppen toevoegen van dit blok: de echte knop en die in het balkje. */
function addButtons(root) {
  const buttons = [...root.querySelectorAll('[data-au-add-buy]')];
  const bar = barOf(root);
  if (bar) buttons.push(...bar.querySelectorAll('[data-au-add-buy]'));
  return buttons;
}

/** @param {HTMLElement} root */
function barOf(root) {
  const bar = /** @type {any} */ (root).auBar;
  return bar instanceof HTMLElement ? bar : null;
}

/** Prijs in het balkje: keuze 1 de prijs van dit product, keuze 2 de ritualprijs na korting. */
function syncBar(root) {
  const bar = barOf(root);
  if (!bar) return;
  const price = bar.querySelector('[data-au-bar-price]');
  if (!price) return;
  const source = mode(root) === 'ritual'
    ? root.querySelector('[data-au-total]')
    : root.querySelector('.au-buy__option .au-buy__price');
  if (source) price.textContent = source.textContent.trim();
}

/**
 * Balkje aan of uit: aan zodra de echte knop Add to cart boven uit beeld is
 * (onderkant boven de bovenrand van het scherm), anders uit.
 * @param {HTMLElement} root
 */
function updateBar(root) {
  const bar = barOf(root);
  const button = root.querySelector('.au-buy__buttons') || root.querySelector('.au-buy__add');
  if (!bar || !(button instanceof HTMLElement)) return;
  const on = button.getBoundingClientRect().bottom < 0;
  bar.classList.toggle('au-bar--on', on);
  bar.setAttribute('aria-hidden', String(!on));
  /* de body weet dat het balkje in beeld is: het chatwolkje van Shopify Inbox
     en de onderkant van de pagina schuiven dan de hoogte van het balkje op */
  const offset = parseFloat(getComputedStyle(bar).getPropertyValue('--au-bar-offset')) || 0;
  document.body.style.setProperty('--au-bar-h', `${bar.offsetHeight + offset}px`);
  document.body.classList.toggle('au-bar-open', on);
  liftChat(bar, on);
}

/**
 * Het chatwolkje van Shopify Inbox boven het balkje in plaats van erop (Jesse, 17 oktober
 * 2026). De chatlaag (shopify-chat, over het hele scherm) negeert een eigen onderkant; daarom
 * schuift de css (au-ritual.css) hem omhoog met --au-chat-lift. Gemeten: waar het balkje
 * straks staat (berekend uit bottom en hoogte, dus ook tijdens het inschuiven en met de
 * veilige zone van de iPhone) en waar het wolkje zonder verschuiving staat. Liggen ze boven
 * elkaar, dan zo ver omhoog dat het wolkje de afstand van het widget (minstens 8px) boven het
 * balkje staat; staat het wolkje ernaast (computer: balkje in het midden), dan niets.
 * @param {HTMLElement} bar
 * @param {boolean} on
 */
function liftChat(bar, on) {
  const chat = document.querySelector('shopify-chat');
  const root = chat && chat.shadowRoot;
  const parts = root ? [...root.querySelectorAll('.activator-nudge, .activator')] : [];
  const shown = parts.filter((part) => part.getBoundingClientRect().width > 0);
  let lift = 0;
  if (on && chat && shown.length > 0) {
    const now = new DOMMatrixReadOnly(getComputedStyle(chat).transform).m42;
    const style = getComputedStyle(bar);
    const gap = Math.max(8, parseFloat(style.getPropertyValue('--au-bar-gap')) || 0);
    const barTop = window.innerHeight - (parseFloat(style.bottom) || 0) - bar.offsetHeight;
    const barBox = bar.getBoundingClientRect();
    for (const part of shown) {
      const box = part.getBoundingClientRect();
      if (box.right <= barBox.left || box.left >= barBox.right) continue;
      lift = Math.max(lift, Math.ceil(box.bottom - now - (barTop - gap)));
    }
  }
  document.body.style.setProperty('--au-chat-lift', `${Math.max(0, lift)}px`);
}

/** Zet het balkje in de body en koppel scrollen en meten. */
function initBar(root) {
  const bar = root.querySelector('[data-au-bar]');
  if (!(bar instanceof HTMLElement)) return;
  for (const old of document.body.querySelectorAll(':scope > [data-au-bar]')) old.remove();
  /** @type {any} */ (root).auBar = bar;
  /** @type {any} */ (bar).auRoot = root;
  document.body.append(bar);
  /* direct meten bij elke scroll: één getBoundingClientRect, goedkoop genoeg,
     en zo werkt het ook in een tabblad dat niet in beeld is (geen rAF nodig) */
  const onScroll = () => updateBar(root);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  updateBar(root);
  /* het chatwolkje laadt later dan de pagina: dan nog een keer meten */
  window.setTimeout(onScroll, 2500);
}

/**
 * @param {HTMLElement} root
 * @param {HTMLElement} item
 */
function select(root, item) {
  const box = item.closest('[data-au-stepbox]');
  if (!box) return;
  /* sinds 13 oktober 2026: het kaartje blijft staan, alleen naam, prijs en beeld wisselen
     (de lijst zit in het kaartje, dus die mag niet overschreven worden) */
  /* sinds 17 oktober 2026 (au_ritual_cards): het hele kaartje uit de template van het product */
  const view = box.querySelector('[data-au-card-view]');
  const tpl = item.querySelector('template[data-au-item-view]');
  const card = item.querySelector('[data-au-item-card]');
  if (view && tpl instanceof HTMLTemplateElement) view.replaceChildren(tpl.content.cloneNode(true));
  else if (view && card) view.replaceChildren(...[...card.childNodes].map((node) => node.cloneNode(true)));
  /* ook de stap van dit product is te wisselen: dan telt het gekozen product, niet de pagina */
  box.removeAttribute('data-au-fixed');
  const title = box.querySelector('[data-au-card-title]');
  if (title) title.textContent = item.getAttribute('data-au-title') || '';
  const price = box.querySelector('[data-au-card-price]');
  if (price) price.textContent = item.getAttribute('data-au-price-text') || '';
  const media = box.querySelector('[data-au-card-media]');
  if (media instanceof HTMLElement) {
    const url = item.getAttribute('data-au-url');
    if (url) media.setAttribute('href', url);
    const img = media.querySelector('img');
    const src = item.getAttribute('data-au-img');
    if (img instanceof HTMLImageElement && src) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = src;
    }
  }
  box.setAttribute('data-au-variant', item.getAttribute('data-au-variant') || '');
  box.setAttribute('data-au-price', item.getAttribute('data-au-price') || '0');
  for (const other of box.querySelectorAll('[data-au-item]')) other.classList.toggle('au-buy__item--on', other === item);
  const list = box.querySelector('[data-au-list]');
  const change = box.querySelector('[data-au-change]');
  if (list) list.hidden = true;
  if (change) change.setAttribute('aria-expanded', 'false');
  syncTotal(root);
}

/**
 * @param {HTMLElement} root
 * @param {'single'|'ritual'} [force] - alleen dit product (linker knop) of de keuze van het blok
 */
async function add(root, force) {
  const buttons = addButtons(root);
  const left = root.querySelector('[data-au-pick="single"]');
  if (left instanceof HTMLElement) buttons.push(left);
  const button = buttons[0];
  const error = root.querySelector('[data-au-error]');
  if (!(button instanceof HTMLElement) || button.getAttribute('aria-busy') === 'true') return;

  const items = [];
  if ((force || mode(root)) === 'ritual') {
    for (const box of root.querySelectorAll('[data-au-stepbox]')) {
      const id = box.hasAttribute('data-au-fixed') ? currentVariant(root) : box.getAttribute('data-au-variant');
      if (id) items.push({ id: Number(id), quantity: 1 });
    }
  } else {
    items.push({ id: Number(currentVariant(root)), quantity: 1 });
  }
  if (!items.length) return;

  for (const each of buttons) each.setAttribute('aria-busy', 'true');
  if (error) error.hidden = true;

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: cartSections(), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'cart add failed');

    const cart = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' }).then((result) => result.json());
    document.dispatchEvent(
      new CartAddEvent(cart, 'au-ritual-buy', {
        source: 'au-ritual-buy',
        itemCount: items.length,
        sections: data.sections,
      })
    );
  } catch (problem) {
    console.error('[au-ritual]', problem);
    if (error) {
      error.textContent = root.dataset.auErrorText || String(problem);
      error.hidden = false;
    }
  } finally {
    for (const each of buttons) each.removeAttribute('aria-busy');
  }
}


document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  /* een open uitklaplijst gaat dicht bij een klik erbuiten (ook buiten het blok) */
  for (const open of document.querySelectorAll('[data-au-list]:not([hidden])')) {
    const wrap = open.parentElement;
    if (wrap && !wrap.contains(target)) {
      open.hidden = true;
      const button = wrap.querySelector('[data-au-change]');
      if (button) button.setAttribute('aria-expanded', 'false');
    }
  }
  let root = target.closest('[data-au-buy]');
  const bar = target.closest('[data-au-bar]');
  if (bar) root = /** @type {any} */ (bar).auRoot || null;
  if (!(root instanceof HTMLElement)) return;

  const pick = target.closest('[data-au-pick]');
  if (pick instanceof HTMLElement) {
    event.preventDefault();
    if (pick.getAttribute('data-au-pick') === 'ritual') {
      /* rechts: ritual open of weer dicht, voegt niets toe */
      root.dataset.auMode = mode(root) === 'ritual' ? 'single' : 'ritual';
      syncMode(root);
    } else {
      /* links: dit product meteen in het mandje, de ritual gaat dicht */
      root.dataset.auMode = 'single';
      syncMode(root);
      add(root, 'single');
    }
    return;
  }

  const change = target.closest('[data-au-change]');
  if (change instanceof HTMLElement) {
    event.preventDefault();
    const box = change.closest('[data-au-stepbox]');
    const list = box && box.querySelector('[data-au-list]');
    if (list instanceof HTMLElement) {
      list.hidden = !list.hidden;
      change.setAttribute('aria-expanded', String(!list.hidden));
    }
    return;
  }

  const item = target.closest('[data-au-item]');
  if (item instanceof HTMLElement) {
    event.preventDefault();
    if (!item.hasAttribute('disabled')) select(root, item);
    return;
  }

  if (target.closest('[data-au-add-buy]')) {
    event.preventDefault();
    add(root);
  }
});

function init() {
  for (const root of document.querySelectorAll('[data-au-buy]')) {
    if (!(root instanceof HTMLElement) || root.dataset.auReady) continue;
    root.dataset.auReady = '1';
    initBar(root);
    syncMode(root);
    syncTotal(root);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
document.addEventListener('shopify:section:load', init);
